import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { parseError } from '@/lib/errors'
import { DriverStatus, useDrivers } from '@/models/driver'
import { EquipmentStatus, useTrailers, useTrucks } from '@/models/equipment'
import {
  type PolicyChangeInput,
  PolicyChangeKind,
  type PolicyChangeLine,
  type PolicyChangeRequest,
  policyChangeWrites,
  usePolicyChangeMutation,
} from '@/models/policyChange'
import { driverOption, equipmentOption } from '../Claims/ClaimStep2Fields'
import { AddDriverForm } from './AddDriverForm'
import { AddVehicleForm } from './AddVehicleForm'
import { ChangeLineItem } from './ChangeLineItem'
import { DescribedChangeForm } from './DescribedChangeForm'
import { EditChange } from './EditChange'
import { EditUnitForm } from './EditUnitForm'
import { PickUnitForm, type UnitOption } from './PickUnitForm'
import { KIND_LABELS } from './policyChangeCopy'

const Kind = PolicyChangeKind
const PICK_KINDS = [Kind.REMOVE_TRUCK, Kind.REMOVE_TRAILER, Kind.REMOVE_DRIVER]

interface ChangeCardsProps {
  orgId: string
  request: PolicyChangeRequest
  /** Kinds to show a card for even before the request has one of them. */
  kinds: PolicyChangeKind[]
}

/**
 * One card per kind of change: the changes added so far, and a form to add another. Its saves share the
 * policy change write key, so a page can hold its own buttons while one is in flight.
 */
export function ChangeCards({ orgId, request, kinds }: ChangeCardsProps) {
  const addChange = usePolicyChangeMutation(orgId, policyChangeWrites.addChange)
  const removeChange = usePolicyChangeMutation(orgId, policyChangeWrites.removeChange)
  const updateChange = usePolicyChangeMutation(orgId, policyChangeWrites.updateChange)
  const saving = addChange.isPending || removeChange.isPending || updateChange.isPending
  const [editing, setEditing] = useState<PolicyChangeLine | null>(null)
  const { trucks } = useTrucks({ orgId, shouldFetchAllPages: true })
  const { trailers } = useTrailers({ orgId, shouldFetchAllPages: true })
  const { drivers } = useDrivers({ orgId, shouldFetchAllPages: true })

  const unitsByKind = useMemo(() => {
    const live = <T extends { status: string; pendingChange?: unknown }>(units: T[], status: string) =>
      units.filter((unit) => unit.status === status && !unit.pendingChange)
    const truckOptions: UnitOption[] = live(trucks, EquipmentStatus.ACTIVE).map((truck) => ({
      ...equipmentOption(truck),
      value: `truck:${truck.id}`,
      type: 'truck',
    }))
    const trailerOptions: UnitOption[] = live(trailers, EquipmentStatus.ACTIVE).map((trailer) => ({
      ...equipmentOption(trailer),
      value: `trailer:${trailer.id}`,
      type: 'trailer',
    }))
    const driverOptions: UnitOption[] = live(drivers, DriverStatus.ACTIVE).map((driver) => ({
      ...driverOption(driver),
      value: `driver:${driver.id}`,
      type: 'driver',
    }))
    return {
      [Kind.REMOVE_TRUCK]: truckOptions,
      [Kind.REMOVE_TRAILER]: trailerOptions,
      [Kind.CHANGE_VEHICLE]: [...truckOptions, ...trailerOptions],
      [Kind.REMOVE_DRIVER]: driverOptions,
      [Kind.CHANGE_DRIVER]: driverOptions,
    } as Partial<Record<PolicyChangeKind, UnitOption[]>>
  }, [trucks, trailers, drivers])

  const coverages = useMemo(
    () => [...new Set(request.policies.flatMap((policy) => policy.coverages))],
    [request.policies]
  )

  async function add(change: PolicyChangeInput): Promise<boolean> {
    try {
      await addChange.mutateAsync({ requestId: request.id, change })
      return true
    } catch (error) {
      toast.error(parseError(error))
      return false
    }
  }

  // The roster's forms show the API's errors on their fields, so this one lets the error through.
  async function addUnit(change: PolicyChangeInput): Promise<void> {
    await addChange.mutateAsync({ requestId: request.id, change })
  }

  // Rejects with the API's error, so the roster's forms can show it on their fields.
  async function save(change: PolicyChangeInput): Promise<void> {
    if (!editing) return
    await updateChange.mutateAsync({ requestId: request.id, changeId: editing.id, change })
    setEditing(null)
  }

  function remove(changeId: string) {
    removeChange.mutate(
      { requestId: request.id, changeId },
      { onError: (error) => toast.error(parseError(error)) }
    )
  }

  function form(kind: PolicyChangeKind) {
    if (kind === Kind.ADD_TRUCK) return <AddVehicleForm kind={kind} existing={trucks} onAdd={addUnit} />
    if (kind === Kind.ADD_TRAILER) return <AddVehicleForm kind={kind} existing={trailers} onAdd={addUnit} />
    if (kind === Kind.ADD_DRIVER) return <AddDriverForm existing={drivers} onAdd={addUnit} />
    if (kind === Kind.CHANGE_VEHICLE || kind === Kind.CHANGE_DRIVER)
      return (
        <EditUnitForm
          kind={kind}
          options={unitsByKind[kind] ?? []}
          trucks={trucks}
          trailers={trailers}
          drivers={drivers}
          onAdd={addUnit}
        />
      )
    if (PICK_KINDS.includes(kind))
      return <PickUnitForm kind={kind} units={unitsByKind[kind] ?? []} onAdd={add} />
    return <DescribedChangeForm kind={kind} coverages={coverages} onAdd={add} />
  }

  const shownKinds = [...new Set([...kinds, ...request.changes.map((line) => line.kind)])]

  return (
    <>
      {/* One write at a time: each returns the whole request, so a slower one could bring back a removed line. */}
      <fieldset disabled={saving} className="contents">
        {shownKinds.map((kind) => {
          const lines = request.changes.filter((line) => line.kind === kind)
          return (
            <Card key={kind}>
              <CardHeader>
                <CardTitle>
                  {KIND_LABELS[kind]}
                  {lines.length > 0 && (
                    <span className="ml-2 text-sm font-normal text-neutral-500">{lines.length}</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                {lines.map((line) => (
                  <ChangeLineItem
                    key={line.id}
                    line={line}
                    onEdit={line.dropped ? undefined : () => setEditing(line)}
                    onRemove={() => remove(line.id)}
                  />
                ))}
                {form(kind)}
              </CardContent>
            </Card>
          )
        })}
      </fieldset>
      {editing && (
        <EditChange
          key={editing.id}
          line={editing}
          trucks={trucks}
          trailers={trailers}
          drivers={drivers}
          coverages={coverages}
          onSave={save}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  )
}
